// PoliticaPrivacidad.jsx
import React, { useEffect } from "react";
import { Container, Typography, Divider, Box, Link } from "@mui/material";

export default function PoliticaPrivacidad() {
    useEffect(() => {
        // SEO: title
        document.title = "Política de Privacidad - EspacioCompacto";
        // SEO: meta description
        const meta = document.querySelector('meta[name="description"]');
        const description = "Política de privacidad de EspacioCompacto: cómo recogemos, usamos y protegemos los datos de contacto (nombre, apellido, celular, correo) de clientes en Cali, Colombia.";
        if (meta) {
            meta.setAttribute("content", description);
        } else {
            const m = document.createElement("meta");
            m.name = "description";
            m.content = description;
            document.head.appendChild(m);
        }

        // JSON-LD (Organization + ContactPoint)
        const jsonLd = {
            "@context": "https://schema.org",
            "@type": "Organization",
            "name": "EspacioCompacto",
            "url": "https://espaciocompacto.com",
            "contactPoint": [{
                "@type": "ContactPoint",
                "email": "info@Martha.com",
                "contactType": "customer service",
                "areaServed": "CO"
            }],
            "address": {
                "@type": "PostalAddress",
                "addressLocality": "Cali",
                "addressCountry": "Colombia"
            }
        };
        const scriptId = "jsonld-politica-privacidad";
        let script = document.getElementById(scriptId);
        if (script) script.remove();
        script = document.createElement("script");
        script.type = "application/ld+json";
        script.id = scriptId;
        script.text = JSON.stringify(jsonLd);
        document.head.appendChild(script);

        return () => {
            // cleanup (optional)
            const s = document.getElementById(scriptId);
            if (s) s.remove();
        };
    }, []);

    return (
        <Container maxWidth="md" sx={{ py: 6, pt:16 }}>
            <Typography variant="h4" component="h1" gutterBottom>
                Política de Privacidad
            </Typography>
            <Typography variant="subtitle1" gutterBottom>
                EspacioCompacto — Remodelación de cocinas, baños y closets (Cali, Colombia)
            </Typography>
            <Divider sx={{ my: 2 }} />

            <Box component="section" sx={{ mb: 3 }}>
                <Typography variant="h6">1. Introducción</Typography>
                <Typography paragraph>
                    En <strong>EspacioCompacto</strong> (https://espaciocompacto.com) respetamos tu privacidad y nos comprometemos a proteger tus datos personales.
                    Esta política explica qué datos recopilamos, con qué fines, cómo los tratamos y los derechos que te asisten.
                </Typography>
            </Box>

            <Box component="section" sx={{ mb: 3 }}>
                <Typography variant="h6">2. Responsable del tratamiento</Typography>
                <Typography paragraph>
                    Responsable: <strong>EspacioCompacto</strong>.<br />
                    Sitio web: <Link href="https://espaciocompacto.com">https://espaciocompacto.com</Link><br />
                    Correo de contacto: <Link href="mailto:info@Martha.com">info@Martha.com</Link><br />
                    Ubicación: Cali, Colombia.
                </Typography>
            </Box>

            <Box component="section" sx={{ mb: 3 }}>
                <Typography variant="h6">3. Datos que recopilamos</Typography>
                <Typography paragraph>
                    Recopilamos datos que tú nos facilitas voluntariamente a través del formulario de contacto o mensajes:
                </Typography>
                <ul>
                    <li>Nombre</li>
                    <li>Apellido</li>
                    <li>Celular</li>
                    <li>Correo electrónico</li>
                    <li>Mensaje / detalles del proyecto (si aplica)</li>
                </ul>
                <Typography paragraph>
                    No recopilamos datos de pago ni gestionamos ventas online en este sitio.
                </Typography>
            </Box>

            <Box component="section" sx={{ mb: 3 }}>
                <Typography variant="h6">4. Finalidades del tratamiento</Typography>
                <Typography paragraph>
                    Usamos tus datos para:
                </Typography>
                <ol>
                    <li>Responder a tus consultas y solicitar/coordinar servicios de remodelación.</li>
                    <li>Enviar información relevante sobre tu solicitud (presupuestos, seguimiento).</li>
                    <li>Mejorar la calidad del servicio y la comunicación.</li>
                </ol>
            </Box>

            <Box component="section" sx={{ mb: 3 }}>
                <Typography variant="h6">5. Legitimación</Typography>
                <Typography paragraph>
                    La base legal para el tratamiento es el consentimiento que otorgas al enviar el formulario y la ejecución de actuaciones precontractuales a petición tuya (interés legítimo del responsable para responder solicitudes).
                </Typography>
            </Box>

            <Box component="section" sx={{ mb: 3 }}>
                <Typography variant="h6">6. Conservación de los datos</Typography>
                <Typography paragraph>
                    Conservaremos tus datos el tiempo necesario para cumplir con la finalidad para la que se recabaron y para cumplir obligaciones legales o resolver posibles reclamaciones. Si el tratamiento se basa en el consentimiento, los datos se conservarán hasta que lo retires o solicites su supresión.
                </Typography>
            </Box>

            <Box component="section" sx={{ mb: 3 }}>
                <Typography variant="h6">7. Destinatarios</Typography>
                <Typography paragraph>
                    No compartimos tus datos con terceros salvo cuando sea necesario para prestar el servicio (por ejemplo, proveedores técnicos que soportan el formulario) o cuando exista una obligación legal. En caso de usar proveedores externos, exigimos contratos que garanticen confidencialidad y medidas técnicas y organizativas adecuadas.
                </Typography>
            </Box>

            <Box component="section" sx={{ mb: 3 }}>
                <Typography variant="h6">8. Seguridad</Typography>
                <Typography paragraph>
                    Implementamos medidas técnicas y organizativas razonables para proteger tus datos contra accesos no autorizados, pérdida o divulgación. Sin embargo, ningún sistema es 100% seguro; te recomendamos tomar precauciones en la comunicación de información sensible.
                </Typography>
            </Box>

            <Box component="section" sx={{ mb: 3 }}>
                <Typography variant="h6">9. Derechos del interesado</Typography>
                <Typography paragraph>
                    Tienes derecho a solicitar:
                </Typography>
                <ol>
                    <li>Acceso a tus datos.</li>
                    <li>Rectificación de datos inexactos.</li>
                    <li>Supresión (derecho al olvido) cuando proceda.</li>
                    <li>Limitación del tratamiento.</li>
                    <li>Portabilidad de los datos (cuando proceda).</li>
                    <li>Oposición al tratamiento por motivos legítimos.</li>
                </ol>
                <Typography paragraph>
                    Para ejercer tus derechos o presentar consultas, escríbenos a <Link href="mailto:info@Martha.com">info@Martha.com</Link>.
                </Typography>
            </Box>

            <Box component="section" sx={{ mb: 3 }}>
                <Typography variant="h6">10. Menores</Typography>
                <Typography paragraph>
                    No solicitamos ni recopilamos conscientemente datos de menores de edad. Si detectamos que se han facilitado datos de menores sin consentimiento parental, los eliminaremos cuando tengamos conocimiento.
                </Typography>
            </Box>

            <Box component="section" sx={{ mb: 3 }}>
                <Typography variant="h6">11. Cambios en la política</Typography>
                <Typography paragraph>
                    Nos reservamos el derecho a actualizar esta política. Cuando hagamos cambios significativos, lo comunicaremos en el sitio web con la fecha de la última revisión.
                </Typography>
            </Box>

            <Divider sx={{ my: 2 }} />

            <Typography variant="body2" color="text.secondary" paragraph>
                Última actualización: {new Date().toLocaleDateString()}.
            </Typography>

            <Box sx={{ mt: 4, p: 2, bgcolor: "background.paper", borderRadius: 1 }}>
                <Typography variant="subtitle2">Aviso:</Typography>
                <Typography variant="body2">
                    Este texto tiene carácter informativo y constituye una propuesta de política de privacidad adaptada a los datos facilitados. No sustituye el asesoramiento legal profesional. Para adaptarla completamente a requisitos legales específicos (por ejemplo, Ley de Protección de Datos de Colombia / Habeas Data), consulta con un abogado.
                </Typography>
            </Box>
        </Container>
    );
}
